import { ReactNode, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import InputWithTitleSection from '@components/InputWithTitleSection'
import { ShortListCustomField, Shortlist } from '@modules/shortlist/types'

import { ValidationMessage } from '../ShortlistTable/CustomColumnPopover/index.styles'

export interface EditCustomColumnSectionProps {
  onSuccess?: (hasError: boolean) => void
  // eslint-disable-next-line
  onSave?: (customField: ShortListCustomField, closePopup: boolean) => void
  onChange?: (name: string) => void
  children?: ReactNode
  middleButtonOnClick?: (cb: () => void) => void
  middleButtonText?: string
  submitButtonText?: string
  subTitle?: string
  title?: Nullable<string>
  selectable?: boolean
  checked?: boolean
  onSelect?: () => void
  hideInput?: boolean
  activeShortlist?: Shortlist
  customColumn?: Partial<ShortListCustomField>
  onCancel?: () => void
}

const useCreateCustomColumnInput = ({ activeShortlist }: { activeShortlist?: Shortlist }) => {
  const inputRef = useRef<Nullable<HTMLInputElement>>(null)
  const { t } = useTranslation()

  const validate = (name: string, id?: number) => {
    if (!name) return t(translationKeys.CUSTOM_LIST_CUSTOM_COLUMN_NAME_SHOULD_NOT_BE_EMPTY)

    const isUnique = !activeShortlist?.customFields?.some(
      field => field.fieldName?.toLowerCase() === name.toLowerCase() && field.id !== id,
    )

    if (!isUnique) {
      return t(translationKeys.CUSTOM_LIST_CUSTOM_COLUMN_COLUMN_NAME_ALREADY_EXISTS)
    }

    return ''
  }

  return { inputRef, validate }
}

const EditCustomColumnSection = (props: EditCustomColumnSectionProps) => {
  const {
    children,
    submitButtonText,
    middleButtonOnClick,
    middleButtonText,
    onSuccess,
    onChange,
    onCancel,
    subTitle,
    title,
    selectable,
    checked,
    onSelect,
    hideInput,
    activeShortlist,
    customColumn,
  } = props
  const { t } = useTranslation()

  const { inputRef, validate } = useCreateCustomColumnInput({
    activeShortlist,
  })

  const errorRenderer = useCallback((error: string) => <ValidationMessage>{error}</ValidationMessage>, [])
  return (
    <InputWithTitleSection
      // eslint-disable-next-line
      inputRefToParent={ref => (inputRef.current = ref)}
      validate={value => validate(value, customColumn?.id)}
      onSelect={onSelect}
      hideInput={hideInput}
      checked={checked}
      selectable={selectable}
      subTitle={subTitle}
      title={title || t(translationKeys.CUSTOM_COLUMN)}
      label={t(translationKeys.COLUMN_NAME)}
      onChange={onChange}
      initialValue={customColumn?.fieldName}
      value={customColumn?.fieldName}
      onCancel={() => onCancel?.()}
      submitWithError
      onSubmit={(_, __, error) => {
        if (onSuccess) {
          onSuccess(!!error)
        }
      }}
      middleButtonText={middleButtonText}
      middleButtonOnClick={middleButtonOnClick}
      submitButtonText={submitButtonText || (t(translationKeys.CONFIRM_BUTTON) as string)}
      isSubmitButtonEnabled
      subtitleWithDivider={false}
      subtitleVariant="body1"
      maxLength={20}
      errorRenderer={errorRenderer}
    >
      {children}
    </InputWithTitleSection>
  )
}

export default EditCustomColumnSection
